// Huisstijl-/briefpapier-module - gedeelde DTO's (API -> UI). Zet geuploade
// Word/Excel op STREEP-huisstijl/briefpapier + PDF. Zod-request-schemas leven in
// apps/api; hier de response-DTO's, unions en de gedeelde conversie-opties.

// batch-62 briefpapier (FO-459 §D) - FACTUUR = factuur-briefpapier in de bibliotheek.
export type HuisstijlSjabloonType = 'BRIEFPAPIER_DOCX' | 'EXCEL_HUISSTIJL' | 'FACTUUR'
export type HuisstijlConversieStatus = 'WACHT' | 'BEZIG' | 'KLAAR' | 'MISLUKT'
export type HuisstijlBronType = 'WORD' | 'EXCEL'

export const HUISSTIJL_SJABLOON_TYPE_LABELS: Record<HuisstijlSjabloonType, string> = {
  BRIEFPAPIER_DOCX: 'Word-briefpapier',
  EXCEL_HUISSTIJL: 'Excel-opmaakprofiel',
  FACTUUR: 'Factuur-briefpapier',
}

export const HUISSTIJL_STATUS_LABELS: Record<HuisstijlConversieStatus, string> = {
  WACHT: 'In wachtrij',
  BEZIG: 'Bezig',
  KLAAR: 'Klaar',
  MISLUKT: 'Mislukt',
}

export const HUISSTIJL_BRON_TYPE_LABELS: Record<HuisstijlBronType, string> = {
  WORD: 'Word',
  EXCEL: 'Excel',
}

/** Opties bij een conversie (opgeslagen als JSON op HuisstijlConversie.opties). */
export type HuisstijlConversieOpties = {
  documenttitel?: string | null
  documentsubtitel?: string | null
  // Excel: welke werkbladen opmaken (leeg/undefined = alle).
  werkbladen?: string[] | null
  // Wel/geen PDF renderen (default: true voor Word, optioneel voor Excel).
  maakPdf?: boolean | null
  // BV-afzender (voettekst/kop). Vrije tekst of afgeleid van de BV van het sjabloon.
  afzender?: string | null
  // FO-459 (batch-62) - Word: het briefpapier van deze conversie, server-side
  // bepaald uit de kiezer (nooit door de client te zetten; zie routes/huisstijl.ts).
  briefpapier?: HuisstijlConversieBriefpapier | null
}

/** FO-459 - het briefpapier dat een Word-conversie gebruikt (snapshot bij aanvragen). */
export type HuisstijlConversieBriefpapier = {
  /** De toepassing van de resolver: bibliotheekversie of het ingebouwde briefpapier. */
  toepassing: import('./briefpapier').BriefpapierToepassingDTO
  /** True als de gebruiker expliciet koos (kiezer); anders de cascade. */
  expliciet: boolean
  /**
   * Wat de verwerking feitelijk toepaste (gezet bij KLAAR). Wijkt alleen af van
   * `toepassing` als het bibliotheekbestand niet te lezen was (zichtbare terugval).
   */
  toegepast?: import('./briefpapier').BriefpapierToepassingDTO | null
}

export type HuisstijlConfigDTO = {
  id: string
  naam: string
  kleurDonker: string
  kleurAchtergrond: string
  kleurLicht: string
  kleurAccent: string
  fontPrimair: string
  fontDisplay: string
  logoPngDocumentId: string | null
  logoSvgDocumentId: string | null
  actief: boolean
}

export type HuisstijlSjabloonDTO = {
  id: string
  naam: string
  type: HuisstijlSjabloonType
  configId: string
  entityId: string | null
  entityNaam: string | null
  heeftBestand: boolean
  fileName: string | null
  isDefault: boolean
  actief: boolean
  createdAt: string
  // batch-62 briefpapier (FO-459) - variant-as (met/zonder handtekeningvak, factuur).
  variant: import('./briefpapier').HuisstijlSjabloonVariant
}

export type HuisstijlConversieDTO = {
  id: string
  status: HuisstijlConversieStatus
  bronType: HuisstijlBronType
  sjabloonId: string
  sjabloonNaam: string | null
  bronDocumentId: string
  bronBestandsnaam: string | null
  resultaatDocumentId: string | null
  pdfDocumentId: string | null
  opties: HuisstijlConversieOpties | null
  foutmelding: string | null
  batchId: string | null
  aangemaaktDoor: string
  verwerktOp: string | null
  createdAt: string
}

export type HuisstijlConversieBatchDTO = {
  id: string
  status: HuisstijlConversieStatus
  aantalTotaal: number
  aantalKlaar: number
  aantalMislukt: number
  aangemaaktDoor: string
  createdAt: string
  conversies: HuisstijlConversieDTO[]
}

export type HuisstijlStatsDTO = {
  dezeMaand: number
  mislukt: number
}

/** Toegestane upload-extensies voor de huisstijl-module. */
export const HUISSTIJL_WORD_EXTENSIES = ['.doc', '.docx'] as const
export const HUISSTIJL_EXCEL_EXTENSIES = ['.xls', '.xlsx'] as const

/** Leid het brontype af uit een bestandsnaam/extensie (of null als onbekend). */
export function huisstijlBronTypeVoorBestand(fileName: string): HuisstijlBronType | null {
  const lower = fileName.toLowerCase()
  if (HUISSTIJL_WORD_EXTENSIES.some((e) => lower.endsWith(e))) return 'WORD'
  if (HUISSTIJL_EXCEL_EXTENSIES.some((e) => lower.endsWith(e))) return 'EXCEL'
  return null
}
